import {
    PostcardActionAttributes,
    PostcardDataType,
    PostComponentOptionsType,
} from "../Post/Post.types";
import {
    CardBoxed,
    PostCardType,
    PostComponentsSection,
} from "../Post/Post.styles";
import React, { useCallback, useState } from "react";
import { SERVICE_MODALS_OPTIONS_STATES } from "../Modals/ServiceFunctionsModal.types";
import { PostActionButtons } from "../Post/PostActions/PostActionButtons";
import { GroupTypeEnum } from "../../Pages/GroupDetailsPage/Group.types";
import { PostReactionsListModal } from "../Modals/ServiceModals/ReactionsListModal/PostReactionsListModal";
import { GameContentContainer } from "./Timeline/GameContentContainer";
import { shortAndShareEntityOnMobile } from "@infrastructure/Helpers/SharingEntity";
import { isDeviceOutputMobileOrMobileApp } from "../../../Domain/DeviceOutputState";
import { useTimelineData } from "./Hooks/useTimelineData";
import { CommunityCircularProgress } from "@shared/styles";
import { LoadingStack } from "./Timeline/GameTimeline.styles";
import { PostCommentPreviewSection } from "../Post/PostCommentPreviewSection";
import { UserType } from "@shared/types";
import { CommentType } from "../Comment/CommentTypes";
import { isMatchNow, useGameGeneralState } from "./Hooks/useGameGeneralState";
import { GameLinkout } from "./Timeline/GameLinkout";
import { useCommunityNavigation } from "@infrastructure/Hooks/useCommunityNavigation";
import { CommentDrawerTriggerMode } from "../../Pages/GameCommentsDrawer/Hooks/useCommentDrawerTrigger";
import { useGameUrl } from "./Hooks/useGameUrl";
import { WATCH_APP_GAME_URL } from "./Service";

export function Post({ id, data, postActions }: PostComponentOptionsType) {
    const { navigateTo } = useCommunityNavigation();

    const { isLoading, timeline, watchAppApiStatus } = useTimelineData(
        data.id.toString(),
    );
    const gameState = useGameGeneralState(timeline);
    const [label, url] = useGameUrl(gameState, timeline, watchAppApiStatus);
    const navigateToWatchApp = useCallback(
        (mode = CommentDrawerTriggerMode.DEFAULT) => {
            const gameId = timeline?.sportEvent.id;
            if (!gameId) {
                return;
            }
            navigateTo(
                WATCH_APP_GAME_URL.replace(":gameId", gameId.toString()) +
                    url +
                    ([
                        CommentDrawerTriggerMode.OPEN_DRAWER,
                        CommentDrawerTriggerMode.OPEN_DRAWER_INPUT_FOCUSED,
                    ].includes(mode)
                        ? `?action=${mode}`
                        : ""),
            );
        },
        [url, navigateTo, timeline?.sportEvent.id],
    );
    const postActionOpts: PostcardActionAttributes = {
        postId: data.id,
        postData: data.postData as PostcardDataType,
        groupType: data.group?.type as GroupTypeEnum,
        onCommentButtonClick: (e, postId) => {
            e.stopPropagation();
            navigateToWatchApp();
        },
        showCommentButton: data.pageRelated?.showCommentButton,
        isInsidePostDetails: postActions.isInsidePostDetails,
        commentButtonText: "Kommentieren",
        createReactOnPost: postActions.createReactOnPost,
        showReportOption: false,
        onShareButtonClicked,
    };
    const showDesktopSharingOptions = !isDeviceOutputMobileOrMobileApp();

    function onShareButtonClicked() {
        if (showDesktopSharingOptions) {
            postActions.setSelectedPost(selectedPost);
            postActions.setIsPostOptionsModalOpen(true);
            postActions.setModalCurrentAction(
                SERVICE_MODALS_OPTIONS_STATES.SHARING_POST,
            );
        } else {
            shortAndShareEntityOnMobile(
                data.group?.id,
                id.toString(),
                data.group!.name,
            );
        }
    }

    const selectedPost: Pick<
        PostComponentOptionsType,
        "id" | "data" | "postActions"
    > = {
        id: id,
        data: data,
        postActions: postActions,
    };

    const cardContentPostOpts = {
        onClick: () => navigateToWatchApp(),
    };

    const [showReactionsModal, setShowReactionsModal] = useState(false);

    const commentPreviewSectionProps = {
        post: data,
        handleCommentPreviewOptionsClicked:
            postActions.handleCommentPreviewOptionsClicked ??
            function (comment: CommentType) {},
        handleCommentPreviewReportClicked:
            postActions.handleCommentPreviewReportClicked,
        createReactOnPreviewComment: postActions.createReactOnPreviewComment,
        openCommentPreviewOwnerAvatarModal: (
            e: React.MouseEvent<HTMLElement>,
        ) => {
            e.stopPropagation();
            postActions.onAvatarClick?.(
                data.latestComment?.createdBy as UserType,
            );
        },
        onCommentButtonClick: (e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            navigateToWatchApp(
                CommentDrawerTriggerMode.OPEN_DRAWER_INPUT_FOCUSED,
            );
        },
        onCommentPreviewClick: (e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            navigateToWatchApp(CommentDrawerTriggerMode.OPEN_DRAWER);
        },
    };
    const gameStatus = useGameGeneralState(timeline);

    return (
        <>
            {showReactionsModal && (
                <PostReactionsListModal
                    open={showReactionsModal}
                    setOpen={setShowReactionsModal}
                    postId={data.id}
                />
            )}
            <CardBoxed
                mt={postActions.isInsidePostDetails ? "12px" : 0}
                isBoxed={data.pageRelated?.boxed}
                {...cardContentPostOpts}
                type={
                    data.status.isHighlighted
                        ? isMatchNow(gameStatus)
                            ? PostCardType.active
                            : PostCardType.highlighted
                        : PostCardType.normal
                }
            >
                {isLoading ? (
                    <LoadingStack>
                        <CommunityCircularProgress />
                    </LoadingStack>
                ) : (
                    <>
                        <GameContentContainer
                            postId={data.id.toString()}
                            isInPostDetails={false}
                        />
                        <GameLinkout
                            timeline={timeline}
                            watchAppApiStatus={watchAppApiStatus}
                        />
                    </>
                )}
                <PostComponentsSection>
                    <PostActionButtons
                        {...postActionOpts}
                        user={data.user}
                        postReactions={data.postData.postReactions}
                    />
                    {data.pageRelated?.showPreviewComment && (
                        <PostCommentPreviewSection
                            {...commentPreviewSectionProps}
                        />
                    )}
                </PostComponentsSection>
            </CardBoxed>
        </>
    );
}
